import { ProtobufClient } from 'api/protobuf/protobuf-client';
import { goBackendApi } from 'hooks/api/httpApi';
import { mapEveryBigintIntoNumber } from 'utils/map-every-bigint-into-number';
import {
    AllShippingRateDefinitionResponse,
    AllShippingRateDefinitionResponseSchema,
} from '../../../../../../../../../pb/responses/v1/all_shipping_rate_definition_response_pb';
import { MarketDrivenPaymentRate } from '../models/market-driven-payment-mapping';

export const ALL_MAPPED_SHIPPING_RATE_DEFINITIONS_PATH = '/api/v1/settings/mappings/shipping/rate';

/**
 * Every mapped market shipping rate definition — the ones payment methods can be assigned to. A 404
 * means there are none yet, which is an empty list rather than an error. `id` is a `uint64`, hence
 * the `mapEveryBigintIntoNumber` conversion.
 *
 * A definition mapped to several shipping types (e.g. `gls`, `gls_cs` and `gls_hd`) comes back once per
 * type, so the list is de-duplicated by id — payment methods are assigned per definition, not per
 * shipping type. The first occurrence of each id is kept.
 */
export const getAllMappedShippingRateDefinitions = async (): Promise<MarketDrivenPaymentRate[]> => {
    const response: AllShippingRateDefinitionResponse | undefined = await new ProtobufClient(goBackendApi())
        .getRequest(AllShippingRateDefinitionResponseSchema, ALL_MAPPED_SHIPPING_RATE_DEFINITIONS_PATH)
        .catch((error) => {
            if (error.status === 404) return undefined;
            throw error;
        });

    const ratesById = new Map<number, MarketDrivenPaymentRate>();

    (response?.definitions ?? []).forEach((definition) => {
        const rate = mapEveryBigintIntoNumber(definition) as MarketDrivenPaymentRate;

        if (!ratesById.has(rate.id)) ratesById.set(rate.id, rate);
    });

    return [...ratesById.values()];
};
